import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Row, 
  Col, 
  Card, 
  Table, 
  Button, 
  Badge, 
  Alert, 
  Modal, 
  Form, 
  ProgressBar,
  Spinner,
  InputGroup,
  Tab,
  Tabs
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  useGetAssignmentStatusQuery,
  useSetAthleteSalaryMutation,
  useGenerateNewSeasonSalariesMutation,
  useAutoAssignAthletesMutation,
  useManualAssignAthleteMutation,
  useManualUnassignAthleteMutation,
} from '../slices/assignmentsApiSlice';
import { useFetchLeaguesQuery } from '../slices/leaguesApiSlice';
import './PlayerAssignmentScreen.css';
import Icon from '../components/Icon.jsx';
import { Activity, Bot, CheckCircle, Hourglass, Trophy, UserPlus, Users, Wallet, X } from 'lucide-react';

const PlayerAssignmentScreen = ({
  leagueIdOverride = null,
  embedded = false,
  activityManager = null,
  membershipManager = null,
}) => {
  const { leagueId: routeLeagueId } = useParams();
  const leagueId = leagueIdOverride || routeLeagueId;
  const navigate = useNavigate();
  
  // Component state
  const [activeTab, setActiveTab] = useState('overview');
  const [showManualAssignModal, setShowManualAssignModal] = useState(false);
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState('');
  const [manualSalary, setManualSalary] = useState('');
  const [showAutoAssignModal, setShowAutoAssignModal] = useState(false);
  const [rankInputs, setRankInputs] = useState({});
  const [teamCardSelections, setTeamCardSelections] = useState({});

  // API queries and mutations
  const { data: league, isLoading: leagueLoading } = useFetchLeaguesQuery(leagueId);
  const { 
    data: assignmentStatus, 
    isLoading: statusLoading, 
    error: statusError,
    refetch: refetchStatus 
  } = useGetAssignmentStatusQuery(leagueId, {
    skip: !leagueId
  });

  const [setAthleteSalary, { isLoading: setSalaryLoading }] = useSetAthleteSalaryMutation();
  const [generateNewSeasonSalaries, { isLoading: generateNewSeasonSalariesLoading }] = useGenerateNewSeasonSalariesMutation();
  const [autoAssignAthletes, { isLoading: autoAssignLoading }] = useAutoAssignAthletesMutation();
  const [manualAssignAthlete, { isLoading: manualAssignLoading }] = useManualAssignAthleteMutation();
  const [manualUnassignAthlete, { isLoading: manualUnassignLoading }] = useManualUnassignAthleteMutation();

  // Handle authorization errors from the API
  useEffect(() => {
    if (statusError && statusError.status === 403) {
      toast.error('Not authorized as league admin');
      if (!embedded) {
        navigate('/');
      }
    }
  }, [statusError, navigate, embedded]);

  useEffect(() => {
    if (!assignmentStatus?.athletes) {
      return;
    }

    setRankInputs(
      assignmentStatus.athletes.reduce((accumulator, athlete) => {
        accumulator[athlete.id] = athlete.salaryRank ? athlete.salaryRank.toString() : '';
        return accumulator;
      }, {})
    );
  }, [assignmentStatus]);

  useEffect(() => {
    if (!assignmentStatus?.teams) {
      return;
    }

    setTeamCardSelections((current) =>
      assignmentStatus.teams.reduce((accumulator, team) => {
        accumulator[team.id] = current[team.id] || '';
        return accumulator;
      }, {})
    );
  }, [assignmentStatus?.teams]);

  // Handle manual salary setting
  const handleSetSalary = async () => {
    if (!selectedAthlete || !manualSalary) return;

    const salary = parseInt(manualSalary.replace(/[^\d]/g, ''));
    if (salary < 100000 || salary > 15000000) {
      toast.error('Salary must be between $100,000 and $15,000,000');
      return;
    }

    try {
      await setAthleteSalary({
        athleteId: selectedAthlete.id,
        salary,
        leagueId,
      }).unwrap();
      
      toast.success(`Salary set to ${new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0
      }).format(salary)} for ${selectedAthlete.name}`);
      
      setShowSalaryModal(false);
      setSelectedAthlete(null);
      setManualSalary('');
      refetchStatus();
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to set salary');
    }
  };

  // Handle manual assignment
  const handleManualAssign = async () => {
    if (!selectedAthlete || !selectedTeam || !manualSalary) return;

    const salary = parseInt(manualSalary.replace(/[^\d]/g, ''));
    if (salary < 100000 || salary > 15000000) {
      toast.error('Salary must be between $100,000 and $15,000,000');
      return;
    }

    try {
      await manualAssignAthlete({
        athleteId: selectedAthlete.id,
        teamId: selectedTeam,
        salary
      }).unwrap();
      
      const team = assignmentStatus.teams.find(t => t.id === selectedTeam);
      toast.success(`${selectedAthlete.name} assigned to ${team.name}`);
      
      setShowManualAssignModal(false);
      setSelectedAthlete(null);
      setSelectedTeam('');
      setManualSalary('');
      refetchStatus();
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to assign athlete');
    }
  };

  // Handle auto-assignment
  const handleAutoAssign = async () => {
    try {
      const result = await autoAssignAthletes(leagueId).unwrap();
      
      toast.success(`Auto-assignment completed! ${result.summary.successfulAssignments} athletes assigned.`);
      
      if (result.unassignedAthletes.length > 0) {
        toast.warning(`${result.unassignedAthletes.length} athletes could not be assigned due to salary cap constraints.`);
      }
      
      setShowAutoAssignModal(false);
      refetchStatus();
    } catch (error) {
      if (error?.data?.requiresManualSalaryAssignment) {
        toast.error(`${error.data.athletesWithoutActivities.length} athletes need manual salary assignment first`);
      } else {
        toast.error(error?.data?.message || 'Auto-assignment failed');
      }
    }
  };

  const handleManualUnassign = async (athlete) => {
    const assignedTeamId = athlete.assignedTeam || athlete.teamId;
    const currentTeamName = assignmentStatus?.teams.find((team) => team.id === assignedTeamId)?.name || 'this team';

    if (!window.confirm(`Remove ${athlete.name} from ${currentTeamName}?`)) {
      return;
    }

    try {
      await manualUnassignAthlete({
        athleteId: athlete.id,
        teamId: assignedTeamId,
      }).unwrap();
      toast.success(`${athlete.name} removed from ${currentTeamName}`);
      refetchStatus();
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to remove athlete from team');
    }
  };

  const handleTeamCardSelectionChange = (teamId, athleteId) => {
    setTeamCardSelections((current) => ({
      ...current,
      [teamId]: athleteId,
    }));
  };

  const handleNeedSalaryCardClick = () => {
    setActiveTab('overview');

    window.requestAnimationFrame(() => {
      document.getElementById('assignment-athlete-overview')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  };

  const handleTeamCardAssign = async (team) => {
    const athleteId = teamCardSelections[team.id];
    const athlete = assignmentStatus?.athletes?.find((entry) => entry.id === athleteId);

    if (!athlete) {
      toast.error('Choose an athlete to add to this team.');
      return;
    }

    if (!athlete.currentSalary || athlete.currentSalary <= 0) {
      toast.error(`${athlete.name} needs a salary before being added to a team.`);
      return;
    }

    if (athlete.assignedTeam === team.id) {
      toast.info(`${athlete.name} is already on ${team.name}.`);
      return;
    }

    try {
      await manualAssignAthlete({
        athleteId: athlete.id,
        teamId: team.id,
        salary: athlete.currentSalary,
      }).unwrap();

      toast.success(`${athlete.name} added to ${team.name}`);
      setTeamCardSelections((current) => ({
        ...current,
        [team.id]: '',
      }));
      refetchStatus();
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to add athlete to team');
    }
  };

  const handleRankChange = (athleteId, value) => {
    const digitsOnly = value.replace(/[^\d]/g, '');
    setRankInputs((current) => ({
      ...current,
      [athleteId]: digitsOnly,
    }));
  };

  const buildRankingsPayload = () => {
    const athletes = assignmentStatus?.athletes || [];
    const totalAthletes = athletes.length;

    if (!totalAthletes) {
      throw new Error('No athletes are available to rank.');
    }

    const rankings = athletes.map((athlete) => {
      const numericRank = Number(rankInputs[athlete.id]);
      return {
        athleteId: athlete.id,
        athleteName: athlete.name,
        rank: numericRank,
      };
    });

    const missingAthletes = rankings.filter((entry) => !Number.isInteger(entry.rank));
    if (missingAthletes.length > 0) {
      throw new Error(`Enter a rank for every athlete before generating salaries.`);
    }

    const invalidRanks = rankings.filter((entry) => entry.rank < 1 || entry.rank > totalAthletes);
    if (invalidRanks.length > 0) {
      throw new Error(`Ranks must be between 1 and ${totalAthletes}.`);
    }

    const uniqueRanks = new Set(rankings.map((entry) => entry.rank));
    if (uniqueRanks.size !== rankings.length) {
      throw new Error('Each rank must be used exactly once.');
    }

    return rankings.map(({ athleteId, rank }) => ({ athleteId, rank }));
  };

  const handleGenerateNewSeasonSalaries = async () => {
    try {
      const rankings = buildRankingsPayload();
      const result = await generateNewSeasonSalaries({
        leagueId,
        rankings,
      }).unwrap();

      toast.success(
        result.generationMode === 'mixed'
          ? `Generated new season salaries. ${result.returningAthleteCount} returning athlete${result.returningAthleteCount === 1 ? '' : 's'} used previous-season performance and ${result.newAthleteCount} new athlete${result.newAthleteCount === 1 ? '' : 's'} used rank.`
          : `Generated ranked salaries. Rank 1 now targets about ${formatCurrency(result.targetTopSalary)} with ${result.playersPerTeam} roster spots per team.`
      );
      refetchStatus();
    } catch (error) {
      toast.error(error?.data?.message || error.message || 'Failed to generate new season salaries');
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Format salary input
  const formatSalaryInput = (value) => {
    const numericValue = value.replace(/[^\d]/g, '');
    return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const sortedAthletes = useMemo(() => {
    if (!assignmentStatus?.athletes) {
      return [];
    }

    return [...assignmentStatus.athletes].sort((left, right) => {
      const leftRank = Number(rankInputs[left.id] || left.salaryRank || Number.MAX_SAFE_INTEGER);
      const rightRank = Number(rankInputs[right.id] || right.salaryRank || Number.MAX_SAFE_INTEGER);

      if (leftRank !== rightRank) {
        return leftRank - rightRank;
      }

      if ((right.currentSalary || 0) !== (left.currentSalary || 0)) {
        return (right.currentSalary || 0) - (left.currentSalary || 0);
      }

      return left.name.localeCompare(right.name);
    });
  }, [assignmentStatus, rankInputs]);

  const athletesById = useMemo(() => (
    new Map((assignmentStatus?.athletes || []).map((athlete) => [athlete.id, athlete]))
  ), [assignmentStatus]);

  const teamsWithAthletes = useMemo(() => {
    if (!assignmentStatus?.teams) {
      return [];
    }

    return assignmentStatus.teams.map((team) => ({
      ...team,
      athletes: [...(team.athletes || [])]
        .sort((left, right) => left.name.localeCompare(right.name))
        .map((athlete) => ({
          ...athlete,
          teamId: team.id,
          assignedTeam: team.id,
          status: athletesById.get(athlete.id)?.status,
        })),
    }));
  }, [assignmentStatus, athletesById]);

  const teamCardAthleteOptions = useMemo(() => {
    if (!assignmentStatus?.athletes) {
      return [];
    }

    return [...assignmentStatus.athletes].sort((left, right) => left.name.localeCompare(right.name));
  }, [assignmentStatus?.athletes]);

  if (leagueLoading || statusLoading) {
    return (
      <div className="section-modern">
        <div className="container-modern text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading assignment data...</p>
        </div>
      </div>
    );
  }

  if (!league || !assignmentStatus) {
    return (
      embedded ? (
        <Alert variant="danger">
          Failed to load league or assignment data. Please try again.
        </Alert>
      ) : (
        <div className="section-modern">
          <div className="container-modern">
            <Alert variant="danger">
              Failed to load league or assignment data. Please try again.
            </Alert>
          </div>
        </div>
      )
    );
  }

  const content = (
    <>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="text-primary mb-2">Player Assignment Manager</h1>
          <p className="text-secondary mb-0">
            {league.name} - Manage athlete assignments and salary caps
          </p>
        </div>
        {!embedded && (
          <Button 
            variant="outline-secondary" 
            onClick={() => navigate(`/league/${leagueId}`)}
          >
            Back to League
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="modern-card h-100">
            <Card.Body className="text-center">
              <Icon icon={Users} size={24} className="mb-2" />
              <h5>Total Athletes</h5>
              <h3 className="text-primary">{assignmentStatus.summary.totalAthletes}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="modern-card h-100">
            <Card.Body className="text-center">
              <Icon icon={CheckCircle} size={24} className="mb-2 text-success" />
              <h5>Assigned to League Teams</h5>
              <h3 className="text-success">{assignmentStatus.summary.assignedAthletes}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="modern-card h-100">
            <Card.Body className="text-center">
              <Icon icon={Hourglass} size={24} className="mb-2 text-warning" />
              <h5>Unassigned to League</h5>
              <h3 className="text-warning">{assignmentStatus.summary.unassignedAthletes}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card
            className="modern-card h-100 assignment-summary-card-button"
            role="button"
            tabIndex={0}
            onClick={handleNeedSalaryCardClick}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleNeedSalaryCardClick();
              }
            }}
          >
            <Card.Body className="text-center">
              <Icon icon={Wallet} size={24} className="mb-2 text-danger" />
              <h5>Need Salary Setup</h5>
              <h3 className="text-danger">{assignmentStatus.summary.athletesNeedingManualSalary}</h3>
              <div className="small text-secondary">Open athlete salary setup</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Auto-Assignment Section */}
      {!embedded && !assignmentStatus.season && (
        <Alert variant="info" className="mb-4">
          No active season is currently running for this league. You can still set salaries manually or generate
          them from rankings now, and activity-based salary suggestions will become available once a season is active.
        </Alert>
      )}

      {!embedded && assignmentStatus.summary.unassignedAthletes > 0 && (
        <Card className="modern-card mb-4">
          <div className="modern-card-header">
            <h3 className="modern-card-title"><Icon icon={Bot} size={18} className="me-2 align-middle" /> Auto-Assignment</h3>
            <p className="modern-card-subtitle">{assignmentStatus.recommendations.message}</p>
          </div>
          <Card.Body>
            {assignmentStatus.recommendations.canAutoAssign ? (
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <p className="mb-1">
                    <strong>Ready for auto-assignment!</strong> All athletes have salary data.
                  </p>
                  <small className="text-secondary">
                    Athletes will be assigned based on performance and salary cap constraints.
                  </small>
                </div>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => setShowAutoAssignModal(true)}
                  disabled={autoAssignLoading}
                >
                  {autoAssignLoading ? 'Assigning...' : 'Auto-Assign Athletes'}
                </Button>
              </div>
            ) : (
              <Alert variant="warning" className="mb-0">
                <strong>Manual action required:</strong> {assignmentStatus.recommendations.message}
              </Alert>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Tabbed Content */}
      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
        {/* Athletes Tab */}
        <Tab eventKey="overview" title={<span><Icon icon={Users} size={16} className="me-2 align-middle" />Athletes</span>}>
          <Card id="assignment-athlete-overview" className="modern-card">
            <div className="modern-card-header">
              <h3 className="modern-card-title">Athlete Overview</h3>
              <p className="modern-card-subtitle">Rank athletes once, generate exponential salaries, then assign as needed</p>
            </div>
            <Card.Body>
              <Alert variant="primary" className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
                <div>
                  <strong>New season salary generation</strong>
                  <div className="small mt-1">
                    Returning athletes use previous-season performance. New athletes use the ranks shown here. New joiners are auto-ranked when they enter the league, but you can adjust any rank before generating salaries.
                  </div>
                </div>
                <Button
                  variant="primary"
                  onClick={handleGenerateNewSeasonSalaries}
                  disabled={generateNewSeasonSalariesLoading}
                >
                  {generateNewSeasonSalariesLoading ? 'Generating...' : 'Generate New Season Salaries'}
                </Button>
              </Alert>
              <div className="table-responsive">
                <Table hover>
                  <thead>
                    <tr>
                      <th style={{ minWidth: '100px' }}>Rank</th>
                      <th>Athlete</th>
                      <th>Status</th>
                      <th>Current Salary</th>
                      <th>League Team</th>
                      <th>Recent Activities</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedAthletes.map((athlete) => (
                      <tr key={athlete.id}>
                        <td>
                          <Form.Control
                            type="text"
                            inputMode="numeric"
                            value={rankInputs[athlete.id] || ''}
                            onChange={(e) => handleRankChange(athlete.id, e.target.value)}
                            placeholder="#"
                            style={{ maxWidth: '84px' }}
                          />
                        </td>
                        <td>
                          <strong>{athlete.name}</strong>
                        </td>
                        <td>
                          <Badge bg={
                            athlete.status === 'assigned' ? 'success' :
                            athlete.status === 'ready_for_assignment' ? 'primary' :
                            athlete.status === 'can_calculate_salary' ? 'info' :
                            'warning'
                          }>
                            {athlete.status === 'assigned' ? 'Assigned to League Team' :
                             athlete.status === 'ready_for_assignment' ? 'Ready for Assignment' :
                             athlete.status === 'can_calculate_salary' ? 'Can Calculate Salary' :
                             'Unassigned to League Team'}
                          </Badge>
                        </td>
                        <td>
                          {athlete.currentSalary > 0 ? (
                            <div>
                              <div>{formatCurrency(athlete.currentSalary)}</div>
                              <small className="text-muted">
                                {athlete.salarySource === 'previousSeasonMileage' ? 'Generated from previous season performance' :
                                athlete.salarySource === 'ranked' ? 'Generated from rank' :
                                 athlete.salarySource === 'calculated' ? 'Calculated from activity' :
                                 athlete.salarySource === 'manual' ? 'Manual override' :
                                 'Salary set'}
                              </small>
                            </div>
                          ) : athlete.suggestedSalary ? (
                            <span className="text-muted">
                              Suggested: {formatCurrency(athlete.suggestedSalary)}
                            </span>
                          ) : (
                            <span className="text-muted">Not set</span>
                          )}
                        </td>
                        <td>
                          {athlete.isAssigned ? (
                            <Badge bg="secondary">
                              {assignmentStatus.teams.find(t => t.id === athlete.assignedTeam)?.name || 'Unknown'}
                            </Badge>
                          ) : (
                            <span className="text-muted">Personal Team Only</span>
                          )}
                        </td>
                        <td>
                          <Badge bg={athlete.hasSeasonActivities ? 'success' : 'secondary'}>
                            {athlete.seasonActivityCount} activities
                          </Badge>
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            {!athlete.hasManualSalary && athlete.hasSeasonActivities && (
                              <Button
                                size="sm"
                                variant="outline-info"
                                onClick={() => {
                                  setSelectedAthlete(athlete);
                                  setManualSalary(athlete.suggestedSalary ? athlete.suggestedSalary.toString() : '');
                                  setShowSalaryModal(true);
                                }}
                              >
                                Override Salary
                              </Button>
                            )}
                            {athlete.status === 'needs_manual_salary' && (
                              <Button
                                size="sm"
                                variant="outline-warning"
                                onClick={() => {
                                  setSelectedAthlete(athlete);
                                  setManualSalary('500000');
                                  setShowSalaryModal(true);
                                }}
                              >
                                Manual Override
                              </Button>
                            )}
                            {!athlete.isAssigned && athlete.currentSalary > 0 && (
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => {
                                  setSelectedAthlete(athlete);
                                  setManualSalary(athlete.currentSalary.toString());
                                  setShowManualAssignModal(true);
                                }}
                              >
                                Assign to League Team
                              </Button>
                            )}
                            {athlete.isAssigned && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  onClick={() => {
                                    setSelectedAthlete(athlete);
                                    setManualSalary(athlete.currentSalary.toString());
                                    setShowManualAssignModal(true);
                                  }}
                                >
                                  Reassign
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleManualUnassign(athlete)}
                                  disabled={manualUnassignLoading}
                                >
                                  {manualUnassignLoading ? 'Removing...' : 'Remove From Team'}
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Tab>

        {/* Teams Tab */}
        <Tab eventKey="teams" title={<span><Icon icon={Trophy} size={16} className="me-2 align-middle" />Teams</span>}>
          <Card className="modern-card">
            <div className="modern-card-header">
              <h3 className="modern-card-title">Team Salary Cap Status</h3>
              <p className="modern-card-subtitle">Monitor team capacity and review every athlete assigned to each league team</p>
            </div>
            <Card.Body>
              <div className="table-responsive">
                <Table hover>
                  <thead>
                    <tr>
                      <th>Team</th>
                      <th>Athletes</th>
                      <th>Salary Usage</th>
                      <th>Cap Utilization</th>
                      <th>Remaining Cap</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assignmentStatus.teams.map((team) => {
                      const utilizationPercent = (team.currentUsage / team.salaryCap) * 100;
                      return (
                        <tr key={team.id}>
                          <td>
                            <strong>{team.name}</strong>
                          </td>
                          <td>
                            <Badge bg="secondary">{team.athleteCount} athletes</Badge>
                          </td>
                          <td>
                            {formatCurrency(team.currentUsage)} / {formatCurrency(team.salaryCap)}
                          </td>
                          <td>
                            <div style={{ width: '120px' }}>
                              <ProgressBar
                                now={utilizationPercent}
                                variant={
                                  utilizationPercent > 90 ? 'danger' :
                                  utilizationPercent > 75 ? 'warning' :
                                  'success'
                                }
                                style={{ height: '8px' }}
                              />
                              <small className="text-muted">
                                {utilizationPercent.toFixed(1)}%
                              </small>
                            </div>
                          </td>
                          <td>
                            <span className={team.remainingCap < 1000000 ? 'text-danger' : 'text-success'}>
                              {formatCurrency(team.remainingCap)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              <div className="assignment-team-grid mt-4">
                {teamsWithAthletes.map((team) => (
                  <Card key={team.id} className="assignment-team-card">
                    <Card.Body>
                      <div className="assignment-team-card-header">
                        <div>
                          <div className="assignment-team-owner-label">Team Owner</div>
                          <h4 className="assignment-team-owner-name">{team.ownerName}</h4>
                          <div className="assignment-team-name">{team.name}</div>
                          {team.ownerEmail && (
                            <div className="assignment-team-owner-email">{team.ownerEmail}</div>
                          )}
                        </div>
                        <Badge bg="secondary">{team.athleteCount} athletes</Badge>
                      </div>

                      {team.athletes?.length ? (
                        <div className="table-responsive assignment-team-table-wrap">
                          <Table size="sm" className="assignment-team-table mb-0">
                            <thead>
                              <tr>
                                <th>Athlete</th>
                                <th>Username</th>
                                <th>Email</th>
                                <th>Other Teams</th>
                              </tr>
                            </thead>
                            <tbody>
                              {team.athletes.map((athlete) => (
                                <tr key={athlete.id}>
                                  <td>
                                    <div className="assignment-team-athlete-cell">
                                      <span className="assignment-team-athlete-name">{athlete.name}</span>
                                      <Button
                                      size="sm"
                                      variant="link"
                                      className="assignment-team-remove-btn"
                                      onClick={() => handleManualUnassign(athlete)}
                                      disabled={manualUnassignLoading}
                                      aria-label={`Remove ${athlete.name} from ${team.name}`}
                                    >
                                        {manualUnassignLoading ? '...' : <Icon icon={X} size={14} />}
                                      </Button>
                                    </div>
                                  </td>
                                  <td>{athlete.username || 'No username'}</td>
                                  <td>{athlete.email || 'No email'}</td>
                                  <td>
                                    <Badge bg="light" text="dark" pill>
                                      {athlete.otherTeamsCount}
                                    </Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      ) : (
                        <div className="assignment-team-empty">No athletes assigned to this team yet.</div>
                      )}

                      <div className="assignment-team-add-row">
                        <Form.Select
                          size="sm"
                          value={teamCardSelections[team.id] || ''}
                          onChange={(event) => handleTeamCardSelectionChange(team.id, event.target.value)}
                          className="assignment-team-add-select"
                        >
                          <option value="">Select athlete to add...</option>
                          {teamCardAthleteOptions.map((athlete) => {
                            const assignedTeamName = athlete.assignedTeam
                              ? assignmentStatus.teams.find((entry) => entry.id === athlete.assignedTeam)?.name
                              : null;

                            return (
                              <option key={athlete.id} value={athlete.id}>
                                {athlete.name}
                                {athlete.currentSalary > 0 ? '' : ' - no salary'}
                                {assignedTeamName ? ` - on ${assignedTeamName}` : ''}
                              </option>
                            );
                          })}
                        </Form.Select>
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => handleTeamCardAssign(team)}
                          disabled={manualAssignLoading || !teamCardSelections[team.id]}
                        >
                          {manualAssignLoading ? 'Adding...' : 'Add'}
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Tab>
        {membershipManager && (
          <Tab eventKey="membership" title={<span><Icon icon={UserPlus} size={16} className="me-2 align-middle" />Membership &amp; Invites</span>}>
            {membershipManager}
          </Tab>
        )}
        {activityManager && (
          <Tab eventKey="activities" title={<span><Icon icon={Activity} size={16} className="me-2 align-middle" />Activities</span>}>
            {activityManager}
          </Tab>
        )}
      </Tabs>
    </>
  );

  return (
    <div className={embedded ? '' : 'section-modern assignment-page-section'}>
      <div className={embedded ? '' : 'container-modern assignment-page-container'}>
        <div className="fade-in">
          {content}
        </div>
      </div>

      {/* Manual Salary Modal */}
      <Modal show={showSalaryModal} onHide={() => setShowSalaryModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Set Salary Override</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAthlete && (
            <div>
              <p><strong>Athlete:</strong> {selectedAthlete.name}</p>
              {selectedAthlete.salaryRank && (
                <p><strong>Current Rank:</strong> {selectedAthlete.salaryRank}</p>
              )}
              {selectedAthlete.suggestedSalary && (
                <p><strong>Suggested Salary:</strong> {formatCurrency(selectedAthlete.suggestedSalary)}</p>
              )}
              <Form.Group className="mb-3">
                <Form.Label>Salary Amount</Form.Label>
                <InputGroup>
                  <InputGroup.Text>$</InputGroup.Text>
                  <Form.Control
                    type="text"
                    value={formatSalaryInput(manualSalary)}
                    onChange={(e) => setManualSalary(e.target.value)}
                    placeholder="500,000"
                  />
                </InputGroup>
                <Form.Text className="text-muted">
                  Range: $100,000 - $15,000,000
                </Form.Text>
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSalaryModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSetSalary}
            disabled={setSalaryLoading}
          >
            {setSalaryLoading ? 'Setting...' : 'Set Salary'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Manual Assignment Modal */}
      <Modal show={showManualAssignModal} onHide={() => setShowManualAssignModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Manually Assign Athlete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAthlete && (
            <div>
              <p><strong>Athlete:</strong> {selectedAthlete.name}</p>
              
              <Form.Group className="mb-3">
                <Form.Label>Select Team</Form.Label>
                <Form.Select
                  value={selectedTeam}
                  onChange={(e) => setSelectedTeam(e.target.value)}
                >
                  <option value="">Choose a team...</option>
                  {assignmentStatus.teams
                    .filter(team => team.remainingCap >= parseInt(manualSalary.replace(/[^\d]/g, '') || '0'))
                    .map(team => (
                      <option key={team.id} value={team.id}>
                        {team.name} (Remaining: {formatCurrency(team.remainingCap)})
                      </option>
                    ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Salary Amount</Form.Label>
                <InputGroup>
                  <InputGroup.Text>$</InputGroup.Text>
                  <Form.Control
                    type="text"
                    value={formatSalaryInput(manualSalary)}
                    onChange={(e) => setManualSalary(e.target.value)}
                    placeholder="500,000"
                  />
                </InputGroup>
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowManualAssignModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleManualAssign}
            disabled={manualAssignLoading || !selectedTeam}
          >
            {manualAssignLoading ? 'Assigning...' : 'Assign Athlete'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Auto-Assignment Confirmation Modal */}
      <Modal show={showAutoAssignModal} onHide={() => setShowAutoAssignModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Auto-Assignment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            This will automatically assign all unassigned athletes to teams based on:
          </p>
          <ul>
            <li>Any rank-generated, manual, or calculated salaries already saved</li>
            <li>Athlete performance-based salary calculations</li>
            <li>Team salary cap constraints</li>
            <li>Fair distribution algorithm</li>
          </ul>
          <p>
            <strong>{assignmentStatus.summary.athletesReadyForAutoAssign}</strong> athletes 
            are ready for auto-assignment.
          </p>
          <Alert variant="info">
            This action cannot be easily undone. Assignments will need to be manually adjusted if needed.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAutoAssignModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAutoAssign}
            disabled={autoAssignLoading}
          >
            {autoAssignLoading ? 'Auto-Assigning...' : 'Proceed with Auto-Assignment'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

PlayerAssignmentScreen.propTypes = {
  leagueIdOverride: PropTypes.string,
  embedded: PropTypes.bool,
  activityManager: PropTypes.node,
  membershipManager: PropTypes.node,
};

export default PlayerAssignmentScreen;
